import { Menu } from '@base-ui/react/menu'
import { twMerge } from 'tailwind-merge'
import { listPopup } from '../../../lib/list-styles'
import { Interactive } from '../../interactive'
import type { PartProps } from '../../../lib/part-props'

/**
 * Props interface for MenuPopup component.
 * Extends base-ui's Menu.Popup.Props for popup container options.
 */
interface MenuPopupProps extends PartProps<Menu.Popup.Props> {
  className?: string
}

/**
 * MenuPopup - The container for menu items with animations and styling.
 *
 * The shared list popup from `lib/list-styles`, the same surface Select opens.
 *
 * In the overlay renderer it also registers its own bounds. That window is
 * click-through except where the cursor hit-tests inside a registered rect, and
 * a menu portals to `document.body` — so without this, whether its items are
 * clickable depends on the popup happening to land over some other registered
 * rect. Registering here rather than at each call site because the trap is
 * invisible until someone clicks in exactly the wrong place, and `Interactive`
 * is a no-op in every other renderer.
 *
 * @example
 * ```tsx
 * <Menu.Popup>
 *   <Menu.Item>Action 1</Menu.Item>
 *   <Menu.Item>Action 2</Menu.Item>
 * </Menu.Popup>
 * ```
 */
function MenuPopup({ className, children, ...props }: MenuPopupProps) {
  return (
    <Menu.Popup
      className={twMerge(
        listPopup,
        // `relative` is load-bearing for the tracker below: it measures the
        // nearest positioned ancestor, which must be the popup itself and not
        // whatever happens to be positioned above it.
        'relative',
        className,
      )}
      {...props}
    >
      <Interactive>{children}</Interactive>
    </Menu.Popup>
  )
}

export { MenuPopup }
